/*
PW Infinite Scroll Component
  - Wrap around one single child node. Child node will be the scrolled content/list.
  - Also includes a loading 'bar' element that will stick to the bottom of the scrolled container.
    To utilize the loading bar, ensure that a parent element includes the classes ".loading.more" during subsequent page loads
  - WARNING: The "onThresholdMet" function called by this component should guard itself against duplicate/rapid calls.
    The component contains safeguards against multiple redundant calls, but the possibility still exists for it to get into a loop where it fires the load function multiple times in rapid succession.

Options (props)
  - watchResize [bool] (default:true): if true (if attribute/prop is included), the InfiniteScroller will check to verify the container still satisfies the threshold when the window resize event fires.
  - loadThreshold [int]: Distance (in pixels) from bottom of scroll container to the bottom of the scrolled 'viewport', that should trigger the load function when exceeded.
  - isReadyForNextEvent [bool]: Uses to determine whether or not the InfiniteScroller is allowed to trigger the onThresholdMet load function.
  - onThresholdMet [function]: Function reference called by the InfiniteScroller when conditions to laod a new page are satisfied (container has scrolled beyond threshold and isReadyForNextEvent is true).
  - useButton [bool] (default:false): if true the InfiniteScroller will NOT respond to scroll events, instead appending a "Load More" button. This allows the user to deliberately choose when pages are loaded. Ideally expose this to a user preference.
  - endOfList [bool]: Pass "true" to disable paging and display an "end of results" indicator

example configuration:
  <InfiniteScroll
    watchResize={ false }
    loadThreshold={ 300 }
    isReadyForNextEvent={ this.props.isLoading is false }
    onThresholdMet={ this.props.incrementPageFunction }
    endOfList={ false }>

    <ul>
      <li>...</li>
      <li>...</li>
      ...
    </ul>

  </InfiniteScroll>
*/

import _ from 'lodash'
import React from 'react'
import T from 'prop-types'
import ScrollBars from './ScrollBars'

export default class InfiniteScroll extends React.Component {
  static displayName = 'InfiniteScroll'

  static propTypes = {
    autoscrollOnMouseDrag: T.bool, // If true will autoscroll to dragged mouse position
    autoscrollRate: T.number, // px to scroll on each "tick" when autoscrolling is required
    autoscrollTolerance: T.number, // px
    className: T.string,
    endOfList: T.bool,
    horizontal: T.bool,
    isReadyForNextEvent: T.bool,
    loadThreshold: T.number,
    onDragSelection: T.func,
    onScroll: T.func,
    onThresholdMet: T.func.isRequired,
    outside: T.bool,
    slimShady: T.bool,
    slimShadyDarker: T.bool,
    style: T.object,
    useButton: T.bool,
    watchResize: T.bool
  }

  static defaultProps = {
    autoscrollOnMouseDrag: false,
    autoscrollRate: 20,
    autoscrollTolerance: 20,
    loadThreshold: 200,
    useButton: false,
    watchResize: true,
    endOfList: false,
    onScroll: null,
    horizontal: true,
    outside: false,
    isReadyForNextEvent: false,
    onThresholdMet: null,
    className: ''
  }

  constructor(props) {
    super(props)
    this.loadingNextPage = false
  }

  componentDidMount() {
    // Bind resize listener if set in options(props)
    if (this.props.watchResize) {
      window.addEventListener('resize', this.checkIfPageNeeded)
    }
  }

  componentDidUpdate() {
    // if parent view is done loading the scrolled content
    if (this.props.isReadyForNextEvent) {
      // If this scroller was the initiator for the latest load event, reset it's state now
      if (this.loadingNextPage) {
        this.loadingNextPage = false
      }

      this.checkIfPageNeeded() //recursively try to fill the container until scroll threshold is satisfied
    }
  }

  componentWillUnmount() {
    this._isUnmounting = true
    // unbind the resize listener
    window.removeEventListener('resize', this.checkIfPageNeeded)
  }

  getScrolledElement = () => {
    if (!this._isUnmounting) {
      return this._scrollbars.getScrolledElement()
    }
  }

  getScrollPosition = () => {
    if (!this._isUnmounting) {
      return this._scrollbars.getScrollPosition()
    }
  }

  scrollTo = (x, y) => {
    if (!this._isUnmounting) {
      return this._scrollbars.scrollTo(x, y)
    }
  }

  scrollToAnimate = (...args) => {
    if (!this._isUnmounting) {
      this._scrollbars.scrollToAnimate(...args)
    }
  }

  didCrossThreshold = () => {
    const { distanceFromEnd } = this.getScrollPosition()
    return distanceFromEnd <= this.props.loadThreshold
  }

  checkIfPageNeeded = () => {
    // If user scrolled past the specified threshold, and we're not loading a new page already, trigger load events
    if (
      (
        !this._isUnmounting,
        this.didCrossThreshold() &&
          this.loadingNextPage === false &&
          this.props.isReadyForNextEvent
      )
    ) {
      this.loadNextPage()
    }
  }

  onLoadMoreClick = () => {
    if (this.loadingNextPage === false && this.props.isReadyForNextEvent) {
      this.loadNextPage()
    }
  }

  onScrolled = node => {
    if (!this.onScrolledDebounced) {
      this.onScrolledDebounced = _.debounce(() => {
        if (!this.props.useButton) {
          this.checkIfPageNeeded()
        }
      }, 200)
    }
    this.onScrolledDebounced()

    const onScroll = this.props.onScroll
    if (onScroll) onScroll(node)
  }

  loadNextPage = () => {
    this.loadingNextPage = true
    this.props.onThresholdMet() //Trigger passed-in page load function reference
  }

  resetScrollPosition = () => {
    // Utility function exposed to calling component, allows the scroll position to manually reset to zero
    if (!this._isUnmounting) {
      this._scrollbars.resetScrollPosition()
    }
  }

  resetScrollPositionAnimate = () => {
    if (!this._isUnmounting) {
      this._scrollbars.resetScrollPositionAnimate()
    }
  }

  _bindScrollBarsRef = el => {
    this._scrollbars = el
  }

  _onAutoScrollRequested = (deltaX, deltaY) => {
    if (!this._isUnmounting) {
      this._scrollbars.scrollToDelta(deltaX, deltaY)
    }
  }

  render() {
    const { props } = this
    return (
      <div
        className={`infinite_scroll_wrap ${props.className || ''}`}
        style={props.style}
      >
        <ScrollBars
          autoscrollOnMouseDrag={props.autoscrollOnMouseDrag}
          autoscrollRate={props.autoscrollRate}
          autoscrollTolerance={props.autoscrollTolerance}
          classes="infinite_scroll"
          horizontal={props.horizontal}
          onScroll={this.onScrolled}
          outside={props.outside}
          getRef={this._bindScrollBarsRef}
          slimShady={props.slimShady}
          slimShadyDarker={props.slimShadyDarker}
        >
          {props.children}
          {props.useButton
            ? <div
                className={
                  'loading_more_button ' +
                  (props.isReadyForNextEvent ? 'visible' : '')
                }
                onClick={this.onLoadMoreClick}
              >
                Load More...
              </div>
            : null}
          <div className="loading_more_text">Loading...</div>
          <div
            className={
              'end_of_results ' + (props.endOfList ? 'visible' : 'hidden')
            }
          >
            End of Results
          </div>
        </ScrollBars>
        <div className="load_strip" />
      </div>
    )
  }
}
